// Location Detection Service
export interface LocationData {
  country?: string;
  region?: string;
  city?: string;
  timezone?: string;
  ipAddress?: string;
}

interface IPAPIResponse {
  country: string;
  region: string;
  city: string;
  timezone: string;
  query: string; // IP address
}

export const getLocationData = async (): Promise<LocationData> => {
  try {
    // Try to get timezone from browser first
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Use ip-api.com for location detection (free, no API key required)
    const response = await fetch('http://ip-api.com/json/?fields=status,country,region,city,timezone,query');
    
    if (!response.ok) {
      throw new Error('Failed to fetch location data');
    }
    
    const data: IPAPIResponse & { status: string } = await response.json();
    
    if (data.status === 'success') {
      return {
        country: data.country,
        region: data.region,
        city: data.city,
        timezone: data.timezone || timezone,
        ipAddress: data.query,
      };
    } else {
      // Fallback to just timezone if location API fails
      return {
        timezone,
      };
    }
  } catch (error) {
    console.warn('Could not detect location:', error);
    
    // Fallback to just timezone from browser
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return { timezone };
    } catch {
      return {};
    }
  }
};

// Alternative service using ipapi.co (HTTPS, also free)
export const getLocationDataSecure = async (): Promise<LocationData> => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Use ipapi.co for HTTPS support
    const response = await fetch('https://ipapi.co/json/');
    
    if (!response.ok) {
      throw new Error('Failed to fetch location data');
    }
    
    const data = await response.json();
    
    return {
      country: data.country_name,
      region: data.region,
      city: data.city,
      timezone: data.timezone || timezone,
      ipAddress: data.ip,
    };
  } catch (error) {
    console.warn('Could not detect location:', error);
    
    // Fallback to just timezone from browser
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return { timezone };
    } catch {
      return {};
    }
  }
};

export default {
  getLocationData,
  getLocationDataSecure,
};
